import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Plugin, ChartEvent,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { IMetricConfig, IPerformanceBucket } from './performanceMetrics';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

export interface IPerformanceChartHandle {
  /** PNG data URL of the chart as drawn, for exports. */
  toImage: () => string | null;
  size: () => { width: number; height: number };
}

interface IPerformanceChartProps {
  series: IPerformanceBucket[];
  granularity: 'day' | 'month';
  metrics: IMetricConfig[];
}

function axisLabel(key: string, granularity: 'day' | 'month', count: number): string {
  if (granularity === 'month') return moment(key, 'YYYY-MM').format('MMM YYYY');
  // A week reads best by weekday, as in the design; longer ranges need the date.
  return count <= 7 ? moment(key, 'YYYY-MM-DD').format('dddd') : moment(key, 'YYYY-MM-DD').format('MMM D');
}

function popupTitle(key: string, granularity: 'day' | 'month'): string {
  return granularity === 'month'
    ? moment(key, 'YYYY-MM').format('MMMM YYYY')
    : moment(key, 'YYYY-MM-DD').format('MMM D, YYYY');
}

// Dashed guide under the selected day, like the design's crosshair.
const selectionLinePlugin: Plugin<'line'> = {
  id: 'selectionLine',
  afterDatasetsDraw(chart) {
    const index = (chart.options.plugins as any)?.selectionLine?.index;
    if (index == null) return;
    const x = chart.scales.x.getPixelForValue(index);
    const { top, bottom } = chart.chartArea;
    const { ctx } = chart;
    ctx.save();
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#B8BFCF';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  },
};

const PerformanceChart = forwardRef<IPerformanceChartHandle, IPerformanceChartProps>(({ series, granularity, metrics }, ref) => {
  const chartRef = useRef<ChartJS<'line'> | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // Hover previews a day; a click pins it so the popup stays while the mouse moves on.
  const [hovered, setHovered] = useState<{ index: number; x: number } | null>(null);
  const [pinned, setPinned] = useState<{ index: number; x: number } | null>(null);
  const selected = pinned ?? hovered;

  useImperativeHandle(ref, () => ({
    toImage: () => chartRef.current?.toBase64Image('image/png', 1) ?? null,
    size: () => ({ width: chartRef.current?.width ?? 0, height: chartRef.current?.height ?? 0 }),
  }));

  // Keyed by content, not identity: parents re-render (e.g. the campaigns poll) and
  // hand down equal-but-new arrays, which must not close the popup.
  const metricsKey = metrics.map(m => m.key).join(',');
  useEffect(() => { setPinned(null); setHovered(null); }, [series, metricsKey]);

  useEffect(() => {
    if (!pinned) return;
    const close = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setPinned(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [pinned]);

  const data = useMemo(() => ({
    labels: series.map(b => axisLabel(b.key, granularity, series.length)),
    datasets: metrics.map(m => ({
      label: m.label,
      data: series.map(b => b[m.key]),
      borderColor: m.color,
      backgroundColor: m.color,
      borderWidth: 2,
      tension: 0.4,
      cubicInterpolationMode: 'monotone' as const,
      pointRadius: (ctx: any) => (selected && ctx.dataIndex === selected.index ? 4 : 0),
      pointHoverRadius: 4,
      pointBorderColor: '#FFFFFF',
      pointBorderWidth: 2,
    })),
  }), [series, granularity, metrics, selected]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      selectionLine: { index: selected?.index ?? null },
    } as any,
    scales: {
      x: {
        grid: { display: false },
        border: { color: '#E5E7EB' },
        ticks: { color: '#373D3F', font: { family: 'Helvetica', size: 12 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#F2F4F7' },
        border: { display: false },
        ticks: { color: '#373D3F', font: { family: 'Helvetica', size: 11 }, precision: 0 },
      },
    },
    onClick: (event: ChartEvent, _elements: unknown, chart: ChartJS) => {
      const native = event.native as Event | null;
      if (!native) return;
      const hits = chart.getElementsAtEventForMode(native, 'index', { intersect: false }, false);
      if (!hits.length) { setPinned(null); return; }
      const index = hits[0].index;
      // Clicking the pinned day again unpins it; any other day pins that one.
      setPinned(prev => (prev?.index === index ? null : { index, x: chart.scales.x.getPixelForValue(index) }));
    },
    onHover: (event: ChartEvent, _elements: unknown, chart: ChartJS) => {
      const native = event.native as Event | null;
      if (!native) return;
      const hits = chart.getElementsAtEventForMode(native, 'index', { intersect: false }, false);
      const target = native.target as HTMLElement | null;
      if (target) target.style.cursor = hits.length ? 'pointer' : 'default';
      const index = hits.length ? hits[0].index : null;
      setHovered(prev => {
        if (index === null) return null;
        return prev?.index === index ? prev : { index, x: chart.scales.x.getPixelForValue(index) };
      });
    },
  }), [selected]);

  const bucket = selected ? series[selected.index] : null;
  const wrapWidth = wrapRef.current?.clientWidth ?? 0;
  // Keep the popup inside the chart: flip it to the left of the guide near the right edge.
  const flip = selected ? selected.x > wrapWidth - 360 : false;

  return (
    <ChartWrap ref={wrapRef} onMouseLeave={() => setHovered(null)}>
      <Line ref={chartRef} data={data} options={options as any} plugins={[selectionLinePlugin]} />
      {selected && bucket && (
        // A hover preview must not catch the mouse, or it flickers as the pointer crosses it.
        <Popup
          $interactive={!!pinned}
          style={flip ? { right: wrapWidth - selected.x + 12 } : { left: selected.x + 12 }}
        >
          <PopupTitle>{popupTitle(bucket.key, granularity)}</PopupTitle>
          {metrics.map(m => (
            <PopupRow key={m.key}>
              <PopupLabel style={{ color: m.color }}>
                <m.Icon size={16} strokeWidth={1.5} />
                <span>{m.label}</span>
              </PopupLabel>
              <PopupValue>{bucket[m.key]}</PopupValue>
            </PopupRow>
          ))}
        </Popup>
      )}
    </ChartWrap>
  );
});

PerformanceChart.displayName = 'PerformanceChart';

export default PerformanceChart;

const ChartWrap = styled.div`
  position: relative;
  height: 330px;
  width: 100%;
`;

const Popup = styled.div<{ $interactive: boolean }>`
  position: absolute; top: 0; z-index: 5;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  width: 340px; padding: 12px;
  display: flex; flex-direction: column; gap: 4px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
`;

const PopupTitle = styled.div`
  margin-bottom: 8px;
  font-family: 'InterVariable', 'Inter', NunitoSans, sans-serif; font-size: 12px; line-height: 18px;
  color: #717680;
`;

const PopupRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 4px 0;
`;

const PopupLabel = styled.span`
  display: inline-flex; align-items: center; gap: 8px; min-width: 0;
  svg { flex-shrink: 0; }
  span {
    font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; line-height: 20px;
    color: #717680; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
`;

// Every box is two digits wide, so the column lines up; longer numbers widen theirs.
const PopupValue = styled.span`
  box-sizing: border-box; flex-shrink: 0;
  min-width: 36px; padding: 2px 8px;
  border: 1px solid #F2F4F7; border-radius: 6px;
  text-align: center;
  font-family: 'InterVariable', 'Inter', NunitoSans, sans-serif; font-size: 14px; font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #252B37;
`;
